import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { VideoData } from '@lumen5/luminary';

import { ApiService, Urls } from 'js/src/client';
import { BUTTON_COLORS, SPINNER_ICON } from 'js/src/components/common/ui/Button';
import type { PlainDropdownItem } from 'js/src/components/common/ui/Dropdown';
import { Dropdown } from 'js/src/components/common/ui/Dropdown';
import type { ShareVideoNotification } from 'js/src/data/share-video';
import { getSupportedAspectRatios } from 'js/src/data/templates/templates-selectors';
import { useVideoLimitGate } from 'js/src/hooks/useVideoLimitGate';
import { isVideoLimitError } from 'js/src/hooks/video-limit-error';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import Navigation from 'js/src/libs/navigation';
import type { RootState } from 'js/src/libs/RootState';
import { useLuminarySelector } from 'js/src/luminary/luminary-hooks';

type Props = {
    videoAspectRatio: string;
    videoId: number;
    notifyUser: (data: ShareVideoNotification) => void;
    className?: string;
};

const NOTHING_RESIZING: Readonly<Record<string, boolean>> = {};

const AutoResizeOptions = React.memo(function AutoResizeOptions({
    videoAspectRatio,
    videoId,
    notifyUser,
    className,
}: Props) {
    const [currentlyResizing, setCurrentlyResizing] = useState(NOTHING_RESIZING);

    const templateId = useLuminarySelector((luminary) => luminary.template.id, []);
    const supportedAspectRatios = useSelector((state: RootState) => {
        return getSupportedAspectRatios(state, templateId);
    });
    const { isOverLimit, showVideoLimitModal } = useVideoLimitGate();

    useEffect(() => {
        setCurrentlyResizing(
            Object.fromEntries(supportedAspectRatios.map((aspectRatio) => [aspectRatio, false])),
        );
    }, [supportedAspectRatios]);

    const allOptions = useMemo(
        (): readonly PlainDropdownItem<{ aspectRatio: string }>[] =>
            supportedAspectRatios.map((aspectRatio) => ({
                text: `${VideoData.getAspectRatioDisplayName(aspectRatio)} (${VideoData.getAspectRatioDisplayRatio(aspectRatio)})`,
                icon: currentlyResizing[aspectRatio] ? SPINNER_ICON : undefined,
                id: aspectRatio,
                disabled: currentlyResizing[aspectRatio],
                onSelectData: { aspectRatio },
            })),
        [currentlyResizing, supportedAspectRatios],
    );

    const items = useMemo(
        () => allOptions.filter((option) => option.id !== videoAspectRatio),
        [allOptions, videoAspectRatio],
    );

    const anyResizingHappening = allOptions.some((option) => option.disabled === true);
    const toggleProps = useMemo(
        () => ({
            text: 'Resize',
            color: BUTTON_COLORS.TERTIARY,
            iconLeft: anyResizingHappening ? SPINNER_ICON : 'aspect_ratio',
            className: 'btn-tertiary btn-md',
        }),
        [anyResizingHappening],
    );

    const onSelect = React.useCallback(
        (onSelectData: { aspectRatio: string }) => {
            const aspectRatio = onSelectData.aspectRatio;
            if (currentlyResizing[aspectRatio]) {
                return;
            }
            if (isOverLimit) {
                showVideoLimitModal();
                return;
            }
            setCurrentlyResizing({ ...currentlyResizing, [aspectRatio]: true });
            ApiService.videosDuplicateVideosCreate({
                body: {
                    video_ids: [videoId],
                    with_prefix: true, // adds "Copy of " to the video name
                },
            })
                .then((response) => {
                    const queryParams = new URLSearchParams({
                        target_aspect_ratio: aspectRatio,
                        id: response.data[0].id.toString(),
                    });
                    Navigation.goToLocation(Urls.APP + '?' + queryParams.toString());
                })
                .catch((e: unknown) => {
                    setCurrentlyResizing((prevResizing) => ({
                        ...prevResizing,
                        [aspectRatio]: false,
                    }));
                    // Stale client-side limit check: surface the upgrade modal on the backend's 403.
                    if (isVideoLimitError(e)) {
                        showVideoLimitModal();
                        return;
                    }
                    ErrorReporter.capture(e);
                    notifyUser({
                        msg: 'Oops, something went wrong. Please try again.',
                        type: 'error',
                        source: 'resize',
                    });
                });

            analytics.track('Auto Resize Clicked', {
                aspect_ratio: aspectRatio,
                triggered_from: 'Share Page',
            });
            analytics.trackHSEvent('Auto Resize Clicked', {
                lumen5_auto_resize_click_date: new Date().getTime(),
            });
        },
        [currentlyResizing, isOverLimit, notifyUser, showVideoLimitModal, videoId],
    );

    return (
        <div className={classNames('autoResize', className)} data-testid="autoResize">
            <Dropdown toggleProps={toggleProps} items={items} onItemSelect={onSelect} />
        </div>
    );
});

export default AutoResizeOptions;
